<script setup lang="ts">
import { computed } from 'vue'
import { formatCurrency } from '../utils/currency'
import StackedChartSummary from './StackedChartSummary.vue'
import { type SeriesItem } from './StackedChart.vue'

interface Props {
  carriedInterest: number
  currency: string
  distributedFees: number
  loading?: boolean
  locale?: string
  hurdleRemaining?: number
  interestEarned?: number
  preferredReturn: number
  profit?: number
  returnOfCapital: number
  grossDistributed: number
  pending?: number
}

const props = withDefaults(defineProps<Props>(), {
  locale: 'en-US',
  loading: false,
  profit: 0,
  interestEarned: 0,
  hurdleRemaining: 0,
})

const distributedValue = computed(() => {
  if (Number.isNaN(props.grossDistributed)) {
    return '--'
  }

  return formatCurrency(props.grossDistributed, {
    currency: props.currency,
    locale: props.locale,
    decimals: 0,
  })
})

const series = computed<SeriesItem[]>(() => [
  ...(props.pending ? [{ name: 'Pending', value: props.pending, color: '#E5E7EB', textColor: '#000000' }] : []),
  { name: 'Return of Capital', value: props.returnOfCapital, color: '#408F93', textColor: '#ffffff' },
  { name: 'Preferred Return', value: props.preferredReturn, color: '#4FB8A9', textColor: '#ffffff' },
  ...(props.profit ? [{ name: 'Profit', value: props.profit, color: '#46B37D', textColor: '#ffffff' }] : []),
  ...(props.interestEarned
    ? [{ name: 'Interest Earned', value: props.interestEarned, color: '#46B37D', textColor: '#ffffff' }]
    : []),
  { name: 'Carried Interest', value: props.carriedInterest, color: '#7d8ad0', textColor: '#ffffff' },
  { name: 'Distributed Fees', value: props.distributedFees, color: '#7d8ad0', textColor: '#ffffff' },
  ...(props.hurdleRemaining
    ? [{ name: 'Hurdle Remaining', value: props.hurdleRemaining, color: '#E5E7EB', textColor: '#000000' }]
    : []),
])
</script>

<template>
  <StackedChartSummary
    :currency="currency"
    :loading="loading"
    :locale="locale"
    :series="series"
    title="Distribution Summary"
    totalLabel="Distributed"
    :totalValue="distributedValue"
  />
</template>
